/* eslint-disable @typescript-eslint/no-floating-promises, @typescript-eslint/no-explicit-any, react-hooks/exhaustive-deps, @typescript-eslint/strict-boolean-expressions */

import { useCallback, useEffect, useMemo, useState } from 'react';
import useCleanup from '../../hooks/useCleanup';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { TabContext, TabPanel } from '@mui/lab';
import { InfoButton, Loader, Tabs } from '../../components';
import {
  FixedStrikeTable,
  FixedStrikeProvider,
} from '../../components/implied_vol/FixedStrikeMatrix';
import { FixedStrikeTableSettings } from '../../components/implied_vol/FixedStrikeMatrix/FixedStrikeTableControls';
import {
  TermStructureChartSettings,
  TermStructureZoomOutButton,
  TermStructureDownloadButton,
  TermStructureProvider,
} from '../../components/implied_vol/TermStructure';
import TermStructureChart from '../../components/implied_vol/TermStructure/TermStructureChart';
import {
  VolSkewChartSettings,
  VolSkewZoomOutButton,
  VolSkewDownloadButton,
  VolSkewProvider,
} from '../../components/implied_vol/VolSkew';
import VolSkewChart from '../../components/implied_vol/VolSkew/VolSkewChart';
import {
  VixChartSettings,
  VixZoomOutButton,
  VixDownloadButton,
  VixProvider,
} from '../../components/implied_vol/Vix';
import VixChart from '../../components/implied_vol/Vix/VixChart';
import useHasVixAccess from 'hooks/iVol/useHasVixAccess';
import { VolSkewTypeSwitch } from '../../components/implied_vol/VolSkew/VolSkewTypeSwitch';
import { COMBINED_SIGNALS } from '../../config';
import useAuth from '../../hooks/auth/useAuth';
import usePrices from '../../hooks/equityhub/usePrices';
import {
  stocksState_SUBSCRIBE_TO_POLLING_RENDER,
  pricesState,
  onSymSearchChangeState,
  searchSuggestionsData,
  searchHandlerState,
} from '../../states';
import {
  HiroOverview,
  IVOL_LABEL_MAPPING,
  IVolTab,
  ProductType,
  SearchHandlerFunction,
  SuggestionSearchData,
} from '../../types';
import { useTermStructureConfig } from 'hooks/iVol/useTermStructureConfig';
import { useVolSkewConfig } from 'hooks/iVol/useVolSkewConfig';
import { useVixConfig } from 'hooks/iVol/useVixConfig';
import { useFixedStrikeConfig } from 'hooks/iVol/useFixedStrikeConfig';
import { useImpliedVolatilityConfig } from 'hooks/iVol/useImpliedVolatilityConfig';
import { usePersistedState } from 'hooks/usePersistedState';

export const ImpliedVol = () => {
  useCleanup();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const stocks = useRecoilValue(stocksState_SUBSCRIBE_TO_POLLING_RENDER);
  const { hasAccessToProduct } = useAuth();
  const hasIVolAccess = hasAccessToProduct(ProductType.IMPLIED_VOL);
  const hasVixAccess = useHasVixAccess();
  const tabLabelOptions = useMemo(() => {
    const tabLabelOptions = new Map(IVOL_LABEL_MAPPING);
    if (!hasVixAccess) {
      tabLabelOptions.delete(IVolTab.Vix);
    }
    return tabLabelOptions;
  }, [IVOL_LABEL_MAPPING, hasVixAccess]);

  const [fsConfig, updateFsConfig] = useFixedStrikeConfig('ivol_fs');

  // Term Structure config via nuqs URL state
  const [tsConfig, updateTsConfig] = useTermStructureConfig();

  // Vol Skew config via nuqs URL state
  const [vsConfig, updateVsConfig] = useVolSkewConfig();

  const [ivolConfig, updateIvolConfig] = useImpliedVolatilityConfig();
  const { sym: querySymbol, tab } = ivolConfig;

  // Vix config via nuqs URL state
  const [vixConfig, updateVixConfig] = useVixConfig();

  // Persist all configs to localStorage
  const allConfigs = useMemo(
    () => ({
      ivol: ivolConfig,
      fs: fsConfig,
      ts: tsConfig,
      vs: vsConfig,
      vix: vixConfig,
    }),
    [ivolConfig, fsConfig, tsConfig, vsConfig, vixConfig],
  );

  const restoreConfigs = useCallback(
    (saved: typeof allConfigs) => {
      if (saved.ivol) {
        updateIvolConfig(saved.ivol);
      }
      if (saved.fs) {
        updateFsConfig(saved.fs);
      }
      if (saved.ts) {
        updateTsConfig(saved.ts);
      }
      if (saved.vs) {
        updateVsConfig(saved.vs);
      }
      if (saved.vix) {
        updateVixConfig(saved.vix);
      }
    },
    [
      updateIvolConfig,
      updateFsConfig,
      updateTsConfig,
      updateVsConfig,
      updateVixConfig,
    ],
  );

  usePersistedState('ivol_query_state', allConfigs, restoreConfigs, [
    'ivol_sym',
    'ivol_tab',
    'ivol_fs',
    'ts',
    'vs',
    'vix',
  ]);

  const setPrices = useSetRecoilState(pricesState);
  const setOnSymSearchChange = useSetRecoilState(onSymSearchChangeState);

  const { getPrices } = usePrices();

  const setSuggestionsData = useSetRecoilState(searchSuggestionsData);

  const setSuggestionsHandler = useSetRecoilState(searchHandlerState);

  useEffect(() => {
    // search suggestions data handler
    const suggestionsHandler: SearchHandlerFunction = (val: string): void => {
      updateIvolConfig({ sym: val });
    };

    setSuggestionsHandler(() => suggestionsHandler);
  }, [updateIvolConfig, setSuggestionsHandler]);

  useEffect(() => {
    const result: SuggestionSearchData[] = Array.from(stocks.values()).map(
      (data: HiroOverview) => ({
        symbol: data.instrument,
        name: data.companyName,
      }),
    );

    const suggestions = result.filter(
      (value: { symbol: string; name: string }) =>
        !COMBINED_SIGNALS.has(value.symbol) && value.symbol !== 'Mag7',
    );
    setSuggestionsData(suggestions);
  }, [setSuggestionsData, stocks]);

  useEffect(() => {
    setOnSymSearchChange((_prevValue: any) => {
      return (sym: string) => {
        updateIvolConfig({ sym });
      };
    });

    return () => {
      setOnSymSearchChange(undefined);
    };
  }, [setOnSymSearchChange, updateIvolConfig]);

  useEffect(() => {
    const fetchPrices = async () => {
      setLoading(true);
      const payload = await getPrices([querySymbol]);
      setPrices(payload);
      setLoading(false);
    };
    fetchPrices();
  }, [querySymbol]);

  const fixedOptions = [
    ...(!hasIVolAccess
      ? [
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: 14,
              display: isMobile ? 'none' : 'inline',
            }}
          >
            Access limited to <strong>{querySymbol}</strong>
          </Typography>,
        ]
      : []),
    ...(tab === IVolTab.FixedStrikeMatrix
      ? [<FixedStrikeTableSettings key="fixed-strike-table-settings" />]
      : []),
    ...(tab === IVolTab.TermStructure
      ? [
          <TermStructureZoomOutButton key="term-structure-zoom" />,
          <TermStructureChartSettings key="term-structure-settings" />,
          <TermStructureDownloadButton key="ts-download" />,
        ]
      : []),
    ...(tab === IVolTab.VolSkew
      ? [
          <VolSkewZoomOutButton key="volatility-skew-zoom" />,
          <VolSkewTypeSwitch
            key="volatility-skew-type-switch"
            value={vsConfig.strikeDisplay}
            onChange={(type) => updateVsConfig({ strikeDisplay: type })}
          />,
          <VolSkewChartSettings key="volatility-skew-settings" />,
          <VolSkewDownloadButton key="vs-download" />,
        ]
      : []),
    ...(tab === IVolTab.Vix && hasVixAccess
      ? [
          <VixZoomOutButton key="vix-zoom" />,
          <VixChartSettings key="vix-settings" />,
          <VixDownloadButton key="vix-download" />,
        ]
      : []),
    <InfoButton key={`ivol-info-${tab}`} articleKey={tab} />,
  ];

  return (
    <Box
      sx={{
        transition: '0.5s',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        background: theme.palette.background.paper,
        boxShadow: theme.palette.shadows.paperBoxShadow,
        borderRadius: theme.spacing(3),
        ...(isMobile ? { minHeight: '450px' } : {}),
        paddingBottom: '50px',
      }}
    >
      <Loader isLoading={Array.from(stocks.values()).length === 0 || loading}>
        <FixedStrikeProvider config={fsConfig} onConfigChange={updateFsConfig}>
          <TermStructureProvider
            selectedSym={querySymbol}
            config={tsConfig}
            onConfigChange={updateTsConfig}
          >
            <VolSkewProvider
              selectedSym={querySymbol}
              config={vsConfig}
              onConfigChange={updateVsConfig}
            >
              <VixProvider config={vixConfig} onConfigChange={updateVixConfig}>
                <TabContext value={tab}>
                  <Tabs
                    options={tabLabelOptions}
                    onChange={(_evt, newTab: string) => {
                      updateIvolConfig({ tab: newTab as IVolTab });
                    }}
                    controlProps={{ options: [], fixedOptions }}
                    fontSize={isMobile ? '12px' : '14px'}
                  />
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      overflowX: 'auto',
                      ...(isMobile ? { maxHeight: 'calc(100% - 55px)' } : {}),
                    }}
                  >
                    <TabPanel
                      key={IVolTab.FixedStrikeMatrix}
                      value={IVolTab.FixedStrikeMatrix as string}
                    >
                      <Box
                        sx={{
                          transition: '0.5s',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                          height: '100%',
                        }}
                      >
                        <FixedStrikeTable />
                      </Box>
                    </TabPanel>
                    <TabPanel
                      key={IVolTab.TermStructure}
                      value={IVolTab.TermStructure as string}
                    >
                      <Box
                        sx={{
                          transition: '0.5s',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                          height: '100%',
                        }}
                      >
                        <TermStructureChart />
                      </Box>
                    </TabPanel>
                    <TabPanel
                      key={IVolTab.VolSkew}
                      value={IVolTab.VolSkew as string}
                    >
                      <Box
                        sx={{
                          transition: '0.5s',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                          height: '100%',
                        }}
                      >
                        <VolSkewChart />
                      </Box>
                    </TabPanel>
                    {hasVixAccess && (
                      <TabPanel key={IVolTab.Vix} value={IVolTab.Vix as string}>
                        <Box
                          sx={{
                            transition: '0.5s',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            height: '100%',
                          }}
                        >
                          <VixChart />
                        </Box>
                      </TabPanel>
                    )}
                  </Box>
                </TabContext>
              </VixProvider>
            </VolSkewProvider>
          </TermStructureProvider>
        </FixedStrikeProvider>
      </Loader>
    </Box>
  );
};
